import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, Lock, Check } from "lucide-react";
import { Link } from "react-router-dom";
import countries from "../../../json/CountryFlagCode.json";
import api from "../../../lib/api.ts";
import { useLanguage } from "../../../context/LanguageContext";

const BuyerRegistrationForm = ({
  onSubmit,
  isLoading,
  accountTypes,
  accountType,
  setAccountType,
  initialValues = {},
  isUniquenessValidated = false,
  onProceed,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("AE");
  const [uniquenessErrors, setUniquenessErrors] = useState({
    email: "",
    mobileNo: "",
  });
  const [isCheckingUniqueness, setIsCheckingUniqueness] = useState({
    email: false,
    mobileNo: false,
  });

  const { language, translate } = useLanguage();
  const isRTL = language === "ar";

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      name: initialValues?.name || "",
      email: initialValues?.email || "",
      mobileNo: initialValues?.mobileNo || "",
      // password: initialValues.password || "",
    },
  });

  // Update form values when initialValues change
  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      reset({
        name: initialValues?.name || "",
        email: initialValues?.email || "",
        mobileNo: initialValues?.mobileNo || "",
        // password: initialValues.password || "",
      });
    }
  }, [initialValues, reset]);

  // Function to check uniqueness
  const checkUniqueness = async (field, value) => {
    if (!value || value.trim() === "") {
      setUniquenessErrors((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    setIsCheckingUniqueness((prev) => ({ ...prev, [field]: true }));

    try {
      const payload = { [field]: value };
      await api.post(`/auth/checkunique`, payload);

      // If successful, clear any existing error
      setUniquenessErrors((prev) => ({ ...prev, [field]: "" }));
    } catch (error) {
      console.error(`Error checking ${field} uniqueness:`, error);

      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          setUniquenessErrors((prev) => ({
            ...prev,
            [field]: errorData.message,
          }));
        } else {
          setUniquenessErrors((prev) => ({
            ...prev,
            [field]:
              field === "email"
                ? translate("auth.signup.emailExists")
                : translate("auth.signup.phoneExists"),
          }));
        }
      } else {
        setUniquenessErrors((prev) => ({
          ...prev,
          [field]:
            field === "email"
              ? translate("auth.signup.emailExists")
              : translate("auth.signup.phoneExists"),
        }));
      }
    } finally {
      setIsCheckingUniqueness((prev) => ({ ...prev, [field]: false }));
    }
  };

  // Handle onBlur for email
  const handleEmailBlur = (e) => {
    const email = e.target.value;
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      checkUniqueness("email", email);
    }
  };

  // Handle onBlur for mobile number
  const handleMobileBlur = (e) => {
    const mobileNo = e.target.value;
    if (mobileNo && mobileNo.trim() !== "") {
      checkUniqueness("mobileNo", mobileNo);
    }
  };

  const onSubmitWithDialCode = (data) => {
    const country = countries.find((c) => c.code === selectedCountry);
    const dial = country?.dial_code || "";
    const raw = (data.mobileNo || "").replace(/\s+/g, "");
    const full = `${dial}${raw}`;
    onSubmit({ ...data, mobileNo: full, selectedCountry });
  };

  return (
    <form
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
      onSubmit={handleSubmit(onSubmitWithDialCode)}
      className='w-full'
    >
      <div className='space-y-3'>
        <h3 className='text-2xl capitalize md:text-[28px] font-bold mb-1 text-[#131313] text-center'>
          {translate("auth.signup.createBuyerAccount")}
        </h3>
        <p className='text-[#13131399] capitalize pb-10 text-sm md:text-[16px] text-center'>
          {translate("auth.signup.buyerSubtitle")}
        </p>
      </div>

      {/* Account Type Selection */}
      <div className='mb-4'>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          {accountTypes.map((type) => (
            <label key={type.id} className='cursor-pointer'>
              <input
                type='radio'
                name='accountType'
                value={type.name}
                checked={accountType === type.name}
                onChange={() => setAccountType(type.name)}
                className='hidden'
              />
              <div
                className={`flex items-center p-3 rounded-lg transition-all duration-200 border-2 ${
                  accountType === type.name
                    ? "bg-orange-50 border-orange-500"
                    : "bg-white border-orange-300"
                }`}
              >
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    isRTL ? "ml-3" : "mr-3"
                  } ${
                    accountType === type.name
                      ? "border-orange-500 bg-orange-500"
                      : "border-gray-300"
                  }`}
                >
                  {accountType === type.name && (
                    <Check className='w-3 h-3 text-white' />
                  )}
                </div>
                <span className='font-medium text-gray-700'>
                  {type.name === "Individual"
                    ? translate("auth.signup.individual")
                    : type.name === "Corporate"
                    ? translate("auth.signup.corporate")
                    : type.name}
                </span>
              </div>
            </label>
          ))}
        </div>
        <hr className='my-2 border-gray-300' />
      </div>

      {/* Name */}
      <div className='mb-4'>
        <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
          {translate("auth.signup.fullName")}
        </label>
        <input
          type='text'
          placeholder={translate("auth.signup.fullNamePlaceholder")}
          className={`w-full ${isRTL ? "pr-4 pl-4" : "pl-4 pr-4"} py-3 border ${
            errors.name ? "border-red-500" : "border-[#13131399]"
          } rounded-lg bg-white focus:outline-none`}
          {...register("name", {
            required: translate("auth.signup.nameRequired"),
            minLength: {
              value: 2,
              message: translate("auth.signup.nameMinLength"),
            },
          })}
        />
        {errors.name && (
          <p className='mt-1 text-sm text-red-500'>{errors.name.message}</p>
        )}
      </div>

      {/* Phone Number */}
      <div className='mb-4'>
        <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
          {translate("auth.signup.phoneNumber")}
        </label>
        <div className='flex'>
          <div
            className={`flex w-full h-12 border border-[#13131399] rounded-lg bg-white overflow-hidden focus-within:ring-orange-500 ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            {/* Country Code Select with Flag */}
            <div className='relative'>
              <div
                className={`absolute ${
                  isRTL ? "right-2" : "left-2"
                } top-1/2 transform -translate-y-1/2 w-10 h-5`}
              >
                <img
                  src={`https://flagcdn.com/w40/${selectedCountry.toLowerCase()}.png`}
                  alt='flag'
                  className='w-full h-full object-cover rounded-sm'
                />
              </div>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className={`h-full ${
                  isRTL ? "pr-12 rounded-r-lg" : "pl-12 rounded-l-lg"
                } bg-white focus:outline-none appearance-none cursor-pointer`}
              >
                {countries
                  .sort((a, b) => a.dial_code.localeCompare(b.dial_code))
                  .map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.dial_code}
                    </option>
                  ))}
              </select>
            </div>

            {/* Phone Number Input */}
            <input
              type='tel'
              placeholder={translate("auth.signup.phonePlaceholder")}
              maxLength='15'
              className={`flex-1 px-4 h-full bg-white focus:outline-none ${
                errors.mobileNo || uniquenessErrors.mobileNo
                  ? "border-red-500"
                  : ""
              }`}
              {...register("mobileNo", {
                required: translate("auth.signup.phoneRequired"),
                pattern: {
                  value: /^[0-9\s]+$/,
                  message: translate("auth.signup.phoneInvalid"),
                },
              })}
              onBlur={handleMobileBlur}
            />
          </div>
        </div>
        {errors.mobileNo && (
          <p className='mt-1 text-sm text-red-500'>{errors.mobileNo.message}</p>
        )}
        {uniquenessErrors.mobileNo && (
          <p className='mt-1 text-sm text-red-500'>
            {uniquenessErrors.mobileNo}
          </p>
        )}
      </div>

      {/* Email */}
      <div className='mb-4'>
        <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
          {translate("auth.signup.email")}
        </label>
        <div className='relative'>
          <div className={`absolute ${isRTL ? "right-2" : "left-2"} top-3`}>
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M12 1.25C9.37665 1.25 7.25 3.37665 7.25 6C7.25 8.62335 9.37665 10.75 12 10.75C14.6234 10.75 16.75 8.62335 16.75 6C16.75 3.37665 14.6234 1.25 12 1.25ZM8.75 6C8.75 4.20507 10.2051 2.75 12 2.75C13.7949 2.75 15.25 4.20507 15.25 6C15.25 7.79493 13.7949 9.25 12 9.25C10.2051 9.25 8.75 7.79493 8.75 6Z'
                fill='#888888'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M12 12.25C9.96067 12.25 8.07752 12.7208 6.67815 13.5204C5.3 14.3079 4.25 15.5101 4.25 17C4.25 18.4899 5.3 19.6921 6.67815 20.4796C8.07752 21.2792 9.96067 21.75 12 21.75C14.0393 21.75 15.9225 21.2792 17.3219 20.4796C18.7 19.6921 19.75 18.4899 19.75 17C19.75 15.5101 18.7 14.3079 17.3219 13.5204C15.9225 12.7208 14.0393 12.25 12 12.25ZM5.75 17C5.75 16.2807 6.26701 15.483 7.42236 14.8228C8.55649 14.1747 10.1733 13.75 12 13.75C13.8267 13.75 15.4435 14.1747 16.5776 14.8228C17.733 15.483 18.25 16.2807 18.25 17C18.25 17.7193 17.733 18.517 16.5776 19.1772C15.4435 19.8253 13.8267 20.25 12 20.25C10.1733 20.25 8.55649 19.8253 7.42236 19.1772C6.26701 18.517 5.75 17.7193 5.75 17Z'
                fill='#888888'
              />
            </svg>
          </div>
          <input
            type='text'
            placeholder={translate("auth.signup.emailPlaceholder")}
            className={`w-full ${
              isRTL ? "pr-10 pl-4" : "pl-10 pr-4"
            } py-3 border ${
              errors.email || uniquenessErrors.email
                ? "border-red-500"
                : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("email", {
              required: translate("auth.signup.emailRequired"),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: translate("auth.signup.emailInvalid"),
              },
            })}
            onBlur={handleEmailBlur}
          />
        </div>
        {errors.email && (
          <p className='mt-1 text-sm text-red-500'>{errors.email.message}</p>
        )}
        {uniquenessErrors.email && (
          <p className='mt-1 text-sm text-red-500'>{uniquenessErrors.email}</p>
        )}
      </div>

      {/* Password */}
      <div className='mb-4'>
        <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
          {translate("auth.signup.password")}
        </label>
        <div className='relative'>
          <div className={`absolute ${isRTL ? "right-2" : "left-2"} top-3`}>
            {/* <Lock className='w-5 h-5 text-gray-400' /> */}
            <svg
              width='20'
              height='21'
              viewBox='0 0 20 21'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M6.66663 14.7943C7.12686 14.7943 7.49996 14.4212 7.49996 13.961C7.49996 13.5007 7.12686 13.1276 6.66663 13.1276C6.20639 13.1276 5.83329 13.5007 5.83329 13.961C5.83329 14.4212 6.20639 14.7943 6.66663 14.7943Z'
                fill='#888888'
              />
              <path
                d='M9.99996 14.7943C10.4602 14.7943 10.8333 14.4212 10.8333 13.961C10.8333 13.5007 10.4602 13.1276 9.99996 13.1276C9.53972 13.1276 9.16663 13.5007 9.16663 13.961C9.16663 14.4212 9.53972 14.7943 9.99996 14.7943Z'
                fill='#888888'
              />
              <path
                d='M14.1666 13.961C14.1666 14.4212 13.7935 14.7943 13.3333 14.7943C12.8731 14.7943 12.5 14.4212 12.5 13.961C12.5 13.5007 12.8731 13.1276 13.3333 13.1276C13.7935 13.1276 14.1666 13.5007 14.1666 13.961Z'
                fill='#888888'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M5.62496 7.29431C5.62496 4.87807 7.58372 2.91931 9.99996 2.91931C12.0377 2.91931 13.7515 4.313 14.2372 6.2001C14.3232 6.53438 14.6639 6.73562 14.9982 6.64958C15.3325 6.56355 15.5337 6.22281 15.4477 5.88852C14.8233 3.46256 12.6218 1.66931 9.99996 1.66931C6.89336 1.66931 4.37496 4.18771 4.37496 7.29431V8.37995C4.18593 8.39324 4.00777 8.41055 3.84017 8.43308C3.09008 8.53393 2.45852 8.74967 1.95692 9.25127C1.45532 9.75287 1.23958 10.3844 1.13873 11.1345C1.0416 11.857 1.04161 12.7756 1.04163 13.9152V14.0067C1.04161 15.1464 1.0416 16.065 1.13873 16.7874C1.23958 17.5375 1.45532 18.1691 1.95692 18.6707C2.45852 19.1723 3.09008 19.388 3.84017 19.4889C4.56264 19.586 5.48124 19.586 6.6209 19.586H13.379C14.5187 19.586 15.4373 19.586 16.1598 19.4889C16.9098 19.388 17.5414 19.1723 18.043 18.6707C18.5446 18.1691 18.7603 17.5375 18.8612 16.7874C18.9583 16.065 18.9583 15.1464 18.9583 14.0067V13.9153C18.9583 12.7756 18.9583 11.857 18.8612 11.1345C18.7603 10.3844 18.5446 9.75287 18.043 9.25127C17.5414 8.74967 16.9098 8.53393 16.1598 8.43308C15.4373 8.33595 14.5187 8.33596 13.379 8.33598H6.6209C6.26797 8.33597 5.93623 8.33597 5.62496 8.33885V7.29431ZM4.00673 9.67193C3.39525 9.75414 3.07144 9.90452 2.8408 10.1352C2.61017 10.3658 2.45979 10.6896 2.37758 11.3011C2.29295 11.9305 2.29163 12.7648 2.29163 13.961C2.29163 15.1572 2.29295 15.9914 2.37758 16.6209C2.45979 17.2324 2.61017 17.5562 2.8408 17.7868C3.07144 18.0174 3.39525 18.1678 4.00673 18.25C4.63619 18.3347 5.47045 18.336 6.66663 18.336H13.3333C14.5295 18.336 15.3637 18.3347 15.9932 18.25C16.6047 18.1678 16.9285 18.0174 17.1591 17.7868C17.3898 17.5562 17.5401 17.2324 17.6223 16.6209C17.707 15.9914 17.7083 15.1572 17.7083 13.961C17.7083 12.7648 17.707 11.9305 17.6223 11.3011C17.5401 10.6896 17.3898 10.3658 17.1591 10.1352C16.9285 9.90452 16.6047 9.75414 15.9932 9.67193C15.3637 9.58731 14.5295 9.58598 13.3333 9.58598H6.66663C5.47045 9.58598 4.63619 9.58731 4.00673 9.67193Z'
                fill='#888888'
              />
            </svg>
          </div>
          <input
            type={showPassword ? "text" : "password"}
            placeholder={translate("auth.signup.passwordPlaceholder")}
            className={`w-full ${
              isRTL ? "pr-10 pl-12" : "pl-10 pr-12"
            } py-3 border ${
              errors.password ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("password", {
              required: translate("auth.signup.passwordRequired"),
              minLength: {
                value: 6,
                message: translate("auth.signup.passwordMinLength"),
              },
              pattern: {
                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                message: translate("auth.signup.passwordPattern"),
              },
            })}
          />
          <button
            type='button'
            onClick={() => setShowPassword(!showPassword)}
            className={`absolute ${
              isRTL ? "left-4" : "right-4"
            } top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600`}
          >
            {showPassword ? (
              // <EyeOff className='w-4 h-4' />
              <Eye className='w-4 h-4' />
            ) : (
              // <Eye className='w-4 h-4' />
              <svg
                width='18'
                height='10'
                viewBox='0 0 18 10'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  fill-rule='evenodd'
                  clip-rule='evenodd'
                  d='M17.5795 0.88651C17.8968 1.02248 18.0438 1.38991 17.9078 1.70718L17.3334 1.46098C17.9078 1.70718 17.9079 1.70703 17.9078 1.70718L17.9072 1.70857L17.9063 1.71065L17.9037 1.71678L17.8948 1.73684C17.8873 1.75362 17.8766 1.77713 17.8628 1.80687C17.835 1.86633 17.7945 1.95076 17.7407 2.0561C17.6334 2.26665 17.473 2.56154 17.2571 2.90803C16.9024 3.47723 16.3937 4.19235 15.7184 4.90279L16.5253 5.70973C16.7694 5.95381 16.7694 6.34954 16.5253 6.59362C16.2812 6.83769 15.8855 6.83769 15.6414 6.59362L14.8076 5.7598C14.2579 6.22038 13.6274 6.65267 12.9126 7.00783L13.6906 8.20344C13.8788 8.49277 13.7969 8.87992 13.5076 9.06817C13.2182 9.25643 12.8311 9.1745 12.6428 8.88518L11.7352 7.49027C11.0857 7.70236 10.3826 7.84649 9.62502 7.89816V9.37764C9.62502 9.72282 9.3452 10.0026 9.00002 10.0026C8.65484 10.0026 8.37502 9.72282 8.37502 9.37764V7.89816C7.64083 7.84808 6.95766 7.71115 6.32493 7.50964L5.42987 8.88523C5.24162 9.17456 4.85446 9.25649 4.56514 9.06823C4.27582 8.87998 4.19389 8.49282 4.38214 8.2035L5.14249 7.03495C4.42217 6.68319 3.78659 6.25279 3.2321 5.79286L2.43128 6.59367C2.18721 6.83775 1.79148 6.83775 1.5474 6.59367C1.30332 6.34959 1.30332 5.95387 1.5474 5.70979L2.3172 4.93999C1.63007 4.22358 1.11279 3.49977 0.752389 2.9232C0.533284 2.57267 0.370577 2.27401 0.261649 2.06072C0.207149 1.954 0.166001 1.86843 0.13789 1.80817C0.123831 1.77804 0.113023 1.75421 0.105427 1.73721L0.0964324 1.71689L0.09373 1.71068L0.0928258 1.70859L0.0924852 1.7078C0.0924217 1.70765 0.0922189 1.70718 0.666685 1.46098L0.0924852 1.7078C-0.043487 1.39053 0.103216 1.02248 0.420485 0.88651C0.737493 0.75065 1.10457 0.897266 1.24081 1.21399C1.24086 1.2141 1.24077 1.21388 1.24081 1.21399L1.24161 1.21582L1.24667 1.22725C1.25158 1.23824 1.25958 1.2559 1.27069 1.27971C1.29291 1.32734 1.32755 1.39952 1.37488 1.4922C1.46963 1.67772 1.61476 1.94454 1.81235 2.26064C2.20869 2.8947 2.80972 3.71688 3.62975 4.47897C4.35134 5.14956 5.23557 5.76728 6.29312 6.17415C7.08853 6.48017 7.98789 6.66931 9.00002 6.66931C10.0348 6.66931 10.9518 6.47161 11.7604 6.15332C12.8116 5.73958 13.6898 5.11785 14.4056 4.44597C15.2136 3.68761 15.8058 2.87361 16.1962 2.24698C16.3909 1.93456 16.5339 1.6712 16.6272 1.4882C16.6738 1.39677 16.7079 1.32562 16.7298 1.27868C16.7408 1.25522 16.7486 1.23783 16.7535 1.22701L16.7584 1.21577L16.7589 1.21478C16.7589 1.21469 16.7588 1.21486 16.7589 1.21478M17.5795 0.88651C17.2624 0.750573 16.8949 0.897677 16.7589 1.21478ZM1.24081 1.21399C1.24086 1.2141 1.24077 1.21388 1.24081 1.21399Z'
                  fill='#888888'
                />
              </svg>
            )}
          </button>
        </div>
        {errors.password && (
          <p className='mt-1 text-sm text-red-500'>{errors.password.message}</p>
        )}
      </div>

      {/* Submit button */}
      {!isUniquenessValidated ? (
        <button
          type='submit'
          disabled={isLoading}
          className={`w-full border border-[#F15A29] bg-white text-[#F15A29] py-3 rounded-lg hover:bg-[#F15A29] hover:text-white transition-all duration-200 font-medium mb-4 ${
            isLoading ? "opacity-90 cursor-not-allowed" : ""
          }`}
        >
          {isLoading
            ? translate("auth.signup.processing")
            : translate("auth.signup.createAccount")}
        </button>
      ) : (
        <button
          type='button'
          onClick={onProceed}
          className='w-full border border-[#F15A29] bg-[#F15A29] text-white py-3 rounded-lg hover:bg-[#F15A29] hover:text-white transition-all duration-200 font-medium mb-4'
        >
          {translate("auth.signup.proceedToCompleteProfile")}
        </button>
      )}

      {/* Switch to login */}
      <p className='text-sm text-center text-gray-600'>
        {translate("auth.signup.alreadyHaveAccount")}{" "}
        <Link
          to='/login'
          state={{ selectedTab: "Buyer" }}
          className='text-orange-600 hover:text-orange-700 hover:underline font-medium'
        >
          {translate("auth.signup.signIn")}
        </Link>
      </p>
    </form>
  );
};

export default BuyerRegistrationForm;
